import type { LearningContext } from '~/shared/libs/course-learning/context';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { getPaginationActions } from '~/shared/helpers/redux-helpers';
import { pagesInitialState } from '~/shared/store/slices/course-categories/course-categories-slice';
import type { UserType } from '~/types';

type PaginationUserItem = Readonly<{
  _id: string;
  [key: string]: unknown;
}>;

type UsersPaginationType = Readonly<{
  items: PaginationUserItem[];
  isLoading: boolean;
  pageInfo: Record<string, unknown>;
}>;

type CoursesSliceType = Readonly<{
  userWithCourses: UserType | null;
  studentCourseContext: LearningContext | null;
  selectedStudent: UserType | null;
  currentUser: UserType | null;
  teacher: UserType | null;
  zeroLessonStudent: UserType | null;
  isUserProfile: boolean;
  onlineUsers: string[];
  selectedStudentsGroup: UserType | null;
  students: string[];
  selectedNoLevelUsers: string[];
  usersPagination: UsersPaginationType;
}>;

const initialState: CoursesSliceType = {
  userWithCourses: null,
  studentCourseContext: null,
  currentUser: null,
  selectedStudent: null,
  selectedStudentsGroup: null,
  isUserProfile: false,
  teacher: null,
  onlineUsers: [],
  students: [],
  selectedNoLevelUsers: [],
  zeroLessonStudent: null,
  usersPagination: {
    items: [],
    isLoading: false,
    pageInfo: pagesInitialState,
  },
};

export const usersSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    setOnlineUsers: (state, action: PayloadAction<string[]>) => {
      state.onlineUsers = action.payload;
    },
    setStudents: (state, action: PayloadAction<string[]>) => {
      state.students = action.payload;
    },
    setSelectedNoLevelUsers: (state, action: PayloadAction<string[]>) => {
      state.selectedNoLevelUsers = action.payload;
    },
    setIsUserProfile: (state, action: PayloadAction<boolean>) => {
      state.isUserProfile = action.payload;
    },
    setCurrentUser: (state, action: PayloadAction<UserType | null>) => {
      state.currentUser = action.payload;
    },
    setTeacher: (state, action: PayloadAction<UserType | null>) => {
      state.teacher = action.payload;
    },
    setZeroLessonStudent: (state, action: PayloadAction<UserType | null>) => {
      state.zeroLessonStudent = action.payload;
    },
    setStudentCourseContext: (state, action: PayloadAction<LearningContext | null>) => {
      state.studentCourseContext = action.payload;
    },
    setUserWithCourses: (state, action: PayloadAction<UserType | null>) => {
      state.userWithCourses = action.payload;
    },
    setSelectedStudent: (state, action: PayloadAction<UserType | null>) => {
      state.selectedStudent = action.payload;
    },
    setSelectedStudentsGroup: (state, action: PayloadAction<UserType | null>) => {
      state.selectedStudentsGroup = action.payload;
    },
    setUsersPagination: (state, action: PayloadAction<UsersPaginationType>) => {
      state.usersPagination = action.payload;
    },
    ...getPaginationActions<'usersPagination', PaginationUserItem, CoursesSliceType>('usersPagination'),
  },
});

export const {
  setStudentCourseContext,
  setSelectedStudent,
  setTeacher,
  setIsUserProfile,
  setSelectedStudentsGroup,
  setSelectedNoLevelUsers,
  setStudents,
  setUsersPagination,
  setCurrentUser,
  setOnlineUsers,
  setZeroLessonStudent,
  setUserWithCourses,
} = usersSlice.actions;
